import { resolveInsuranceTypeLabel } from '@/shared/enums/insurance-type.enum';
import { useNavigation, useRequirePermissions } from '@/shared/hooks';
import { Page, PageHeader } from '@/shared/ui/page';
import { toaster } from '@/shared/ui/toaster.component';
import { formatCurrency, formatDateToBrazil } from '@/shared/utils';
import {
  formatRegisterNumber,
  isRegisterNumberCnpjMask,
  resolveRegisterNumber,
} from '@/shared/utils/format-register-number';
import { formatZipcode } from '@/shared/utils/format-zipcode.util';
import { getQuoteStatus } from '@/shared/utils/get-quote-status..util';

import { getProductConfig } from '@/app/config/products.config';
import { resolveCapitalGlobalQuoteWizardRedirect } from '@/features/quotes/components/capital-global/capital-global-quote-product.util';
import { QUOTE_EXPIRATION_DAYS } from '@/app/config/quotes.config';
import { Alert, Button, cn, Spinner } from '@noorden-insurtech/alba-design-system';
import {
  ChevronDown,
  Copy,
  FileCheck,
  FileText,
  Home,
  SquarePen,
} from 'lucide-react';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  QuoteParamsProvider,
  useQuoteParams,
} from '../context/quote-params.context';
import { useValidateInspectionJourney } from '../hooks/homeowner/use-validate-inspection-journey.hook';
import { InspectionJourneyModal } from './quote-steps/inspection-journey-modal.component';

import { coveragesService } from '@/services/coverages';
import type { Coverage } from '@/services/coverages/types';
import type { BusinessRisk } from '@/services/quotes/types';
import { STALE_TIME_FIVE_MINUTES_MS } from '@/shared/constants/query-stale-time.constants';
import { PortalStatus } from '@/shared/enums/portal-status.enum';
import { isClonedQuoteMarkedAsDraft } from '@/shared/utils/clone-quote-status.util';
import { downloadFromSignedUrl } from '@/shared/utils/signed-url.util';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { BrokerInactiveNotice } from '../../brokers/components/broker-inactive-notice.component';
import { useSelectedBrokerWriteGuard } from '../../brokers/hooks';
import { ModalGeneratePDF } from '../../proposals/components/proposal-steps/modal-generate-pdf.component';
import { ModalSendMail } from '../../proposals/components/proposal-steps/modal-send-mail.component';
import { PremiumCard } from '../../proposals/components/proposal-steps/premium-card.component';
import { useQuoteDocument } from '../hooks/business/use-quote-document.hook';
import { useUnderwritingMessages } from '../hooks/business/use-underwriting-messages.hook';
import { useCopyQuote } from '../hooks/common/use-copy-quote.hook';
import { useInitiateProposal } from '../hooks/common/use-initiate-proposal.hook';
import { useConstructionTypes } from '../hooks/homeowner/use-construction-types.hook';
import { usePropertyClassifications } from '../hooks/homeowner/use-property-classifications.hook';
import { usePropertyTypes } from '../hooks/homeowner/use-property-types.hook';
import { useProposalDetails } from '../hooks/homeowner/use-proposal-details.hook';
import { useBusinessActivities } from '../hooks/business/use-business-activities.hook';
import {
  formatUnderwritingMessages,
  resolveBusinessActivityNames,
} from '../utils/format-underwriting-messages.util';
import { mapBusinessRisksToLocations } from '../utils/map-business-risks-to-locations.util';
import { parseProductCode } from '../utils/parse-product-code';
import { type BusinessLocation } from './quote-steps/business-location';
import { BusinessLocationCard } from './quote-steps/business-location-card.component';
import { CloneQuoteDialog } from './clone-quote.dialog';
import { EditableCoverageItem } from './editable-coverage-card.component';
import {
  CommercializationSection,
  InfoRow,
} from './quote-details-sections.component';

type EditableCoverageSection = {
  title: string;
  items: EditableCoverageItem[];
};

type CoverageItem = {
  name: string;
  value?: string;
};

type QuoteDetailsProps = {
  policyId: string;
};

function requiresInspection(
  value?: boolean | 'S' | 'N' | string | null
): boolean {
  return value === true || value === 'S';
}

export function QuoteDetails({ policyId }: QuoteDetailsProps) {
  return (
    <QuoteParamsProvider>
      <QuoteDetailsContent policyId={policyId} />
    </QuoteParamsProvider>
  );
}

function QuoteDetailsContent({ policyId }: QuoteDetailsProps) {
  const navigate = useNavigate();
  const { setParams, productId } = useQuoteParams();

  const initiateProposalMutation = useInitiateProposal();
  const validateInspectionJourneyMutation = useValidateInspectionJourney();
  const copyQuoteMutation = useCopyQuote();
  const [isCloneDialogOpen, setIsCloneDialogOpen] = useState(false);
  const [isInspectionJourneyModalOpen, setIsInspectionJourneyModalOpen] =
    useState(false);
  const [expandedLocationIds, setExpandedLocationIds] = useState<Set<string>>(
    new Set()
  );
  const { hasPermission: canWriteQuote } = useRequirePermissions({
    permissions: ['policies.quotes.write'],
  });
  const { hasPermission: canWriteProposal } = useRequirePermissions({
    permissions: ['policies.proposals.write'],
  });
  const quoteWriteGuard = useSelectedBrokerWriteGuard('quote');
  const proposalWriteGuard = useSelectedBrokerWriteGuard('proposal');
  const quoteActionGuard = useSelectedBrokerWriteGuard('quote', 'other');

  const { push, replace } = useNavigation();

  const {
    data: proposalDetails,
    isLoading: isLoadingProposal,
    refetch: refetchProposalDetails,
  } = useProposalDetails(policyId);

  useEffect(() => {
    if (!proposalDetails) return;
    const resolvedCategory = parseProductCode(proposalDetails.productCode || '');
    setParams({
      policyId,
      id: policyId,
      proposalNumber:
        proposalDetails.proposalNo || proposalDetails.quotationNo || '',
      productId:
        proposalDetails.productId != null
          ? String(proposalDetails.productId)
          : '',
      productCode: proposalDetails.productCode || '',
      category: resolvedCategory,
    });
  }, [proposalDetails, policyId, setParams]);

  useEffect(() => {
    if (!proposalDetails) return;
    const cfg = getProductConfig(proposalDetails.productCode);
    if (cfg?.usesCapitalGlobalProposal) {
      const { stepCode, quoteFlow } = resolveCapitalGlobalQuoteWizardRedirect({
        statusPortal: proposalDetails.statusPortal,
        mode: 'view',
      });

      void navigate({
        to: '/quotes/products/new/$id',
        params: { id: proposalDetails.policyId },
        search: {
          productId: String(proposalDetails.productId),
          category: cfg.schemaCategory,
          productCode: cfg.productCode,
          stepCode,
          quoteFlow,
        },
        replace: true,
      });
      return;
    }

    if (cfg?.usesGroupLifeProposal) {
      void navigate({
        to: '/quotes/products/new/$id',
        params: { id: proposalDetails.policyId },
        search: {
          productId: String(proposalDetails.productId),
          category: cfg.schemaCategory,
          productCode: cfg.productCode,
          stepCode: 'CALCULATION',
          quoteFlow: 'view',
        },
        replace: true,
      });
    }
  }, [proposalDetails, navigate]);

  const resolvedGrossPremium =
    proposalDetails?.grossPremium ??
    proposalDetails?.GrossPremium ??
    proposalDetails?.DuePremium ??
    0;
  const resolvedNetPremium = proposalDetails?.rawPremium;
  const liquidValue =
    resolvedNetPremium != null ? formatCurrency(resolvedNetPremium) : '-';
  const resolvedIof = proposalDetails?.iof ?? proposalDetails?.IOF ?? 0;

  const totalValue = formatCurrency(resolvedGrossPremium);

  const iofValue = resolvedIof;
  const proposalNumber =
    proposalDetails?.quotationNo || proposalDetails?.proposalNo || '';
  const proposalNoLabel = proposalNumber.trim();

  const category = parseProductCode(proposalDetails?.productCode || '');
  const isBusinessQuote = category === 'BUSINESS_INSURANCE';
  const product = isBusinessQuote ? 'Empresarial' : 'Residencial';

  const proponentRegisterNumber = resolveRegisterNumber(
    proposalDetails?.customer?.cpf,
    proposalDetails?.customer?.idNo
  );
  const isProponentCnpj = isRegisterNumberCnpjMask(proponentRegisterNumber);

  const versionDate = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, []);

  // Catálogo de coberturas da modalidade residencial; o endpoint é indexado por
  // `productCode`. Para business, cada local pode ter uma atividade diferente
  // (CNAE), então o `BusinessLocationCard` busca o próprio catálogo por
  // `location.activityType` em vez de receber um catálogo único do pai.
  const { data: coveragesCatalog } = useQuery<Coverage[]>({
    queryKey: ['product-coverages', proposalDetails?.productCode, versionDate],
    enabled: !isBusinessQuote && Boolean(proposalDetails?.productCode),
    staleTime: STALE_TIME_FIVE_MINUTES_MS,
    queryFn: async () => {
      if (!proposalDetails?.productCode) {
        throw new Error('ProductCode não encontrado');
      }
      const result = await coveragesService.getCoverages(
        proposalDetails.productCode,
        versionDate
      );
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Falha ao buscar coberturas');
      }
      return result.data;
    },
  });

  const { data: propertyTypes } = usePropertyTypes();

  const propertyType =
    propertyTypes?.find(
      pt =>
        String(pt.code) === proposalDetails?.lobs?.[0]?.risks?.[0]?.propertyType
    )?.name || '';

  const { data: constructionTypes } = useConstructionTypes();

  const constructionType =
    constructionTypes?.find(
      ct =>
        String(ct.code) ===
        proposalDetails?.lobs?.[0]?.risks?.[0]?.constructionType
    )?.description || '';

  const { data: propertyClassifications } = usePropertyClassifications();

  const propertyClassification =
    propertyClassifications?.find(
      pc =>
        String(pc.code) ===
        proposalDetails?.lobs?.[0]?.risks?.[0]?.propertyClassification
    )?.description || '';

  const portalStatus = Number(proposalDetails?.statusPortal) || 0;
  const isClosed = portalStatus === PortalStatus.QuoteClosed;
  const isExpired = portalStatus === PortalStatus.QuoteExpired;
  const isUnderAnalysis = portalStatus === PortalStatus.QuoteUnderAnalysis;

  const { data: underwritingData } = useUnderwritingMessages(
    policyId,
    isBusinessQuote && isUnderAnalysis && Boolean(policyId)
  );

  // Carta de recusa só existe para cotação empresarial recusada em análise
  // (status QuoteClosed); o GET retorna 404 enquanto a carta não tiver sido
  // gerada — gate na própria query pra evitar requests desnecessários.
  const { data: refusalLetter, isLoading: isRefusalLetterLoading } =
    useQuoteDocument(policyId, 'refusal_letter', {
      enabled: isBusinessQuote && isClosed && Boolean(policyId),
    });

  const handleDownloadRefusalLetter = useCallback(async () => {
    if (!refusalLetter?.url) return;
    try {
      const filename =
        refusalLetter.key.split('/').pop() || `carta-recusa-${policyId}.pdf`;
      await downloadFromSignedUrl(refusalLetter.url, filename);
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Erro ao baixar carta de recusa',
        description:
          error instanceof Error
            ? error.message
            : 'Não foi possível baixar a carta. Tente novamente.',
      });
    }
  }, [refusalLetter, policyId]);

  const { data: businessActivitiesCatalog } = useBusinessActivities({
    enabled: isBusinessQuote && isUnderAnalysis,
  });

  const underAnalysisMessages = useMemo(() => {
    if (!isBusinessQuote || !isUnderAnalysis) return undefined;
    const risks = (proposalDetails?.lobs?.[0]?.risks ?? []) as BusinessRisk[];
    const activityCodes = risks
      .map(
        risk =>
          risk.activityTypePerformed || risk.tipoDeAtividadeRealizada || ''
      )
      .filter(Boolean);
    return formatUnderwritingMessages(underwritingData?.messages ?? [], {
      activities: resolveBusinessActivityNames(
        activityCodes,
        businessActivitiesCatalog
      ),
      hasUnderwritingDiscountRequest: risks.some(
        risk => risk.underwritingDiscountRequestInd === 'Y'
      ),
    });
  }, [
    isBusinessQuote,
    isUnderAnalysis,
    proposalDetails?.lobs,
    underwritingData?.messages,
    businessActivitiesCatalog,
  ]);
  const isTerminalStatus =
    isClosed || isExpired || portalStatus === PortalStatus.QuoteDeleted;
  const resolvedQuoteStatus =
    !isTerminalStatus && isClonedQuoteMarkedAsDraft(proposalDetails?.policyId)
      ? PortalStatus.QuoteInProgress
      : portalStatus;

  const isDisabled = isClosed || isExpired;
  const isInProgress = resolvedQuoteStatus === PortalStatus.QuoteInProgress;

  const canCreateProposal =
    proposalDetails?.quotationDate &&
    DateTime.fromISO(proposalDetails.quotationDate).diffNow('days').as('days') >
    -QUOTE_EXPIRATION_DAYS &&
    portalStatus === PortalStatus.QuoteCalculated;
  // Permite editar cotações em preenchimento ou já calculadas, alinhado com a
  // listagem de cotações.
  const canEditQuote =
    canWriteQuote &&
    (resolvedQuoteStatus === PortalStatus.QuoteInProgress ||
      resolvedQuoteStatus === PortalStatus.QuoteCalculated);

  const canClone =
    resolvedQuoteStatus === PortalStatus.QuoteInProgress ||
    resolvedQuoteStatus === PortalStatus.QuoteCalculated;

  const showCreateProposal = canWriteProposal && !isInProgress && !isDisabled;

  const handleCloseCloneDialog = () => {
    setIsCloneDialogOpen(false);
    copyQuoteMutation.reset();
  };

  const handleConfirmClone = async () => {
    if (!policyId) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível clonar',
        description: 'ID da cotação (PolicyId) não encontrado.',
      });
      handleCloseCloneDialog();
      return;
    }

    try {
      const { policyId: newPolicyId } = await copyQuoteMutation.mutateAsync({
        policyId,
        quoteNumber: proposalDetails?.quotationNo || proposalDetails?.proposalNo,
        productCode: proposalDetails?.productCode,
      });

      toaster.create({
        type: 'success',
        title: 'Cotação clonada com sucesso',
      });

      handleCloseCloneDialog();
      push('/quotes/$policyId/edit', { policyId: newPolicyId });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Erro ao clonar cotação.';

      toaster.create({
        type: 'error',
        title: 'Não foi possível clonar',
        description: message.includes('Deductible')
          ? `${message} (edite a cotação para ajustar franquias e tente clonar novamente)`
          : message,
      });
    }
  };

  const coveragesEditable: EditableCoverageSection[] = useMemo(() => {
    if (!proposalDetails) {
      return [];
    }

    const coveragesFromApi =
      proposalDetails.lobs?.[0]?.risks?.[0]?.coverages || [];

    const resolveCoverageLabel = (code?: string) => {
      const resolvedCode = (code || '').trim();
      if (!resolvedCode) return 'Cobertura';
      const match = coveragesCatalog?.find(cov => cov.code === resolvedCode);
      return match?.name?.trim() || resolvedCode;
    };

    const isExcludedCode = (code?: string) => {
      const upper = (code || '').trim().toUpperCase();
      return (
        upper.endsWith('ASS') ||
        upper.endsWith('CAP') ||
        upper === 'ASSIST' ||
        upper === 'CAPITA'
      );
    };

    const items: EditableCoverageItem[] = coveragesFromApi
      .filter(coverage => !isExcludedCode(coverage.productElementCode))
      .map(coverage => ({
        description: resolveCoverageLabel(coverage.productElementCode),
        limiteLabel: 'Limite Máximo de Indenização*',
        limitePlaceholder: 'R$ 000,00',
        limiteValue: formatCurrency(coverage?.sumInsured ?? 0),
        premioLabel: 'Franquia*',
        premioPlaceholder:
          'Franquia de 1%  Com limite entre R$ 0,01 e R$ 1.000',
        premioValue: formatCurrency(coverage?.deductible ?? 0),
      }));

    return items.length > 0 ? [{ title: 'Coberturas', items }] : [];
  }, [proposalDetails, coveragesCatalog]);

  const assistanceItems = [
    'Chaveiro para Acesso ao Domicílio ( Entrada ou Saída)',
    'Chaveiro para Acesso à Cômodos do Imóvel',
    'Encanador',
    'Eletricista',
    'Vidraceiro',
    'Limpeza do Imóvel pós Sinistro',
    'Vigilante',
    'Cobertura Provisória de Telhados',
    'Regresso Antecipado',
    'Manutenção Geral Consultoria Orçamentária',
  ];

  const assistancePurePremium = useMemo(() => {
    const coverages = proposalDetails?.lobs?.[0]?.risks?.[0]?.coverages ?? [];
    const assistCoverage = coverages.find(
      cov => cov.productElementCode === 'ASSIST'
    );
    if (!assistCoverage) return undefined;
    return formatCurrency(assistCoverage.purePremium ?? 0);
  }, [proposalDetails]);

  const businessLocationCards = useMemo<BusinessLocation[]>(() => {
    if (!isBusinessQuote || !proposalDetails?.lobs?.[0]?.risks?.length) {
      return [];
    }
    return mapBusinessRisksToLocations(
      proposalDetails.lobs[0].risks as BusinessRisk[],
      { stableIds: true }
    );
  }, [isBusinessQuote, proposalDetails]);

  const toggleLocation = (key: string) => {
    setExpandedLocationIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleInitiateProposal = async () => {
    if (!policyId) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível criar a proposta',
        description: 'ID da cotação (PolicyId) não encontrado.',
      });
      return;
    }

    try {
      const data = await initiateProposalMutation.mutateAsync({
        policyId,
      });

      toaster.create({
        type: 'success',
        title: 'Proposta criada com sucesso',
        description: data.proposalNo
          ? `Nº da proposta: ${data.proposalNo}`
          : undefined,
      });
      const proposalPolicyId = data.policyId || policyId;
      replace('/proposals/$id/edit', { id: proposalPolicyId });
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível criar a proposta',
        description: error instanceof Error ? error.message : 'Erro inesperado',
      });
    }
  };

  const handleInspectionJourneyConfirm = async () => {
    setIsInspectionJourneyModalOpen(false);
    await handleInitiateProposal();
  };

  const handleCreateProposal = async () => {
    if (!policyId) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível validar inspeção',
        description: 'ID da cotação (PolicyId) não encontrado.',
      });
      return;
    }

    try {
      await validateInspectionJourneyMutation.mutateAsync(policyId);

      const { data: refreshedProposalDetails } = await refetchProposalDetails();

      if (requiresInspection(refreshedProposalDetails?.requiresInspection)) {
        setIsInspectionJourneyModalOpen(true);
        return;
      }

      await handleInitiateProposal();
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Erro ao validar elegibilidade para inspeção',
        description: error instanceof Error ? error.message : 'Erro inesperado',
      });
    }
  };

  const dedicatedWizardCfg = getProductConfig(
    proposalDetails?.productCode ?? '',
  );
  const usesDedicatedWizard = !!(
    dedicatedWizardCfg?.usesGroupLifeProposal ||
    dedicatedWizardCfg?.usesCapitalGlobalProposal
  );

  if (isLoadingProposal || usesDedicatedWizard) {
    return (
      <Page.Root>
        <div className='flex h-[50vh] items-center justify-center'>
          <Spinner size='lg' />
        </div>
      </Page.Root>
    );
  }

  return (
    <Page.Root>
      <Page.Breadcrumb
        items={[
          {
            label: 'Página Inicial',
            href: '/dashboard',
            iconLeft: <Home className='h-4 w-4' />,
          },
          { label: 'Cotações', href: '/quotes' },
          ...(proposalNoLabel
            ? [{ label: proposalNoLabel, active: true }]
            : [{ label: 'Cotação', active: true }]),
        ]}
      />

      <PageHeader.Root className='gap-2 sm:gap-4'>
        <PageHeader.Main className='gap-2 sm:gap-4'>
          <PageHeader.TitleGroup>
            <PageHeader.Title className='text-xl sm:text-[30px]'>
              Cotação - {product}
            </PageHeader.Title>
            <PageHeader.Subtitle className='text-xs sm:text-sm'>
              Visualize os detalhes da cotação
            </PageHeader.Subtitle>
          </PageHeader.TitleGroup>
          {canWriteQuote && canClone && (
            <PageHeader.Actions>
              <div
                title={quoteWriteGuard.reason}
                className='flex flex-col items-end gap-1'
              >
                <Button
                  variant='ghost'
                  size='large'
                  loading={copyQuoteMutation.isPending}
                  disabled={
                    copyQuoteMutation.isPending || quoteWriteGuard.blocked
                  }
                  onClick={() => setIsCloneDialogOpen(true)}
                >
                  <Copy className='h-5 w-5' strokeWidth={1.6} />
                  Clonar
                </Button>
              </div>
            </PageHeader.Actions>
          )}
        </PageHeader.Main>
      </PageHeader.Root>

      <BrokerInactiveNotice
        guards={[quoteWriteGuard, proposalWriteGuard, quoteActionGuard]}
      />

      <Page.Content className='py-0 sm:py-4'>
        <div className='mb-6 flex flex-col gap-6 rounded-2xl bg-alba-white-500 p-4 shadow-[0px_4px_24px_0px_rgba(15,22,30,0.08)] sm:mb-0 sm:gap-8 sm:p-8'>
          <PremiumCard
            totalValue={totalValue}
            liquidValue={liquidValue}
            interestPercentage=''
            interestValue=''
            iofValue={formatCurrency(iofValue)}
            product={product}
            proposalNumber={proposalNumber}
            status={getQuoteStatus(resolvedQuoteStatus).label}
            showPisCofins={false}
            isUnderAnalysis={
              resolvedQuoteStatus === PortalStatus.QuoteUnderAnalysis
            }
            underAnalysisMessages={underAnalysisMessages}
          />

          {/* Alert de Notificação */}
          {isDisabled && (
            <Alert.Root status='warning' className='items-start'>
              <Alert.Icon className='mt-0.5 shrink-0' />
              <Alert.Content className='flex-1 text-left'>
                <Alert.Text className='text-left'>
                  {isExpired
                    ? 'Esta cotação está expirada e não é mais válida para transmissão ou efetivação. Gere uma nova cotação para prosseguir.'
                    : isBusinessQuote && isClosed && refusalLetter?.url
                      ? 'Esta cotação foi recusada na análise. A carta de recusa está disponível para download abaixo.'
                      : 'Esta cotação está encerrada e não é mais válida para transmissão ou efetivação. Gere uma nova cotação para prosseguir.'}
                </Alert.Text>
              </Alert.Content>
              <Alert.CloseButton className='self-start' />
            </Alert.Root>
          )}

          {/* Informações da Cotação */}
          <div className='flex flex-col gap-6'>
            <p className='font-montserrat text-base font-normal text-alba-black-500'>
              Informações da Cotação
            </p>

            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              {/* Dados da Emissão */}
              <div className='flex flex-col gap-4'>
                <p className='font-montserrat text-sm font-bold text-alba-black-500'>
                  Dados da Emissão
                </p>
                <div className='flex flex-col rounded-xl border border-alba-gray-400 px-4 py-2 divide-y divide-alba-gray-200'>
                  <InfoRow
                    label='Início de Vigência'
                    value={formatDateToBrazil(
                      proposalDetails?.effectiveDate ?? ''
                    )}
                  />
                  <InfoRow
                    label='Fim de Vigência'
                    value={formatDateToBrazil(
                      proposalDetails?.expiryDate ?? ''
                    )}
                  />
                  <InfoRow
                    label='Tipo de Seguro'
                    value={resolveInsuranceTypeLabel(proposalDetails)}
                  />
                  <InfoRow
                    label='Registrado por'
                    value={proposalDetails?.insertByUserName ?? ''}
                  />
                </div>
              </div>

              {/* Dados do Proponente */}
              <div className='flex flex-col gap-4'>
                <p className='font-montserrat text-sm font-bold text-alba-black-500'>
                  Dados do Proponente
                </p>
                <div className='flex flex-col rounded-xl border border-alba-gray-400 px-4 py-2 divide-y divide-alba-gray-200'>
                  {isBusinessQuote ? (
                    <>
                      <InfoRow
                        label={isProponentCnpj ? 'CNPJ' : 'CPF'}
                        value={formatRegisterNumber(proponentRegisterNumber)}
                      />
                      <InfoRow
                        label={isProponentCnpj ? 'Razão Social' : 'Nome'}
                        value={
                          proposalDetails?.customer?.socialName ||
                          proposalDetails?.customer?.name ||
                          ''
                        }
                      />
                    </>
                  ) : (
                    <>
                      <InfoRow
                        label='CPF'
                        value={formatRegisterNumber(
                          proposalDetails?.customer?.cpf ?? ''
                        )}
                      />
                      <InfoRow
                        label='Nome'
                        value={proposalDetails?.customer?.name ?? ''}
                      />
                    </>
                  )}
                </div>
              </div>

              {!isBusinessQuote &&
                proposalDetails?.lobs &&
                proposalDetails?.lobs?.length > 0 && (
                  <>
                    {/* Dados do Imóvel */}
                    <div className='flex flex-col gap-4'>
                      <p className='font-montserrat text-sm font-bold text-alba-black-500'>
                        Dados do Imóvel
                      </p>
                      <div className='flex flex-col rounded-xl border border-alba-gray-400 px-4 py-2 divide-y divide-alba-gray-200'>
                        <InfoRow
                          label='CEP'
                          value={formatZipcode(
                            proposalDetails?.lobs?.[0]?.risks?.[0]?.zipCode ??
                              ''
                          )}
                        />
                        <InfoRow
                          label='Endereço'
                          value={`${proposalDetails?.lobs?.[0]?.risks?.[0]?.address ?? ''}, ${proposalDetails?.lobs?.[0]?.risks?.[0]?.addressNumber ?? ''}`}
                        />
                        <InfoRow
                          label='Bairro'
                          value={
                            proposalDetails?.lobs?.[0]?.risks?.[0]
                              ?.neighborhood ?? ''
                          }
                        />
                        <InfoRow
                          label='Cidade'
                          value={
                            proposalDetails?.lobs?.[0]?.risks?.[0]?.city ?? ''
                          }
                        />
                        <InfoRow
                          label='Estado'
                          value={
                            proposalDetails?.lobs?.[0]?.risks?.[0]?.state ?? ''
                          }
                        />
                      </div>
                    </div>

                    {/* Dados do Risco */}
                    <div className='flex flex-col gap-4'>
                      <p className='font-montserrat text-sm font-bold text-alba-black-500'>
                        Dados do Risco
                      </p>
                      <div className='flex flex-col rounded-xl border border-alba-gray-400 px-4 py-2 divide-y divide-alba-gray-200'>
                        <InfoRow label='Tipo do Imóvel' value={propertyType} />
                        <InfoRow
                          label='Imóvel Tombado?'
                          value={
                            proposalDetails?.lobs?.[0]?.risks?.[0]
                              ?.listedPropertyFlag === 'Y'
                              ? 'Sim'
                              : 'Não'
                          }
                        />
                        <InfoRow
                          label='Tipo de Construção'
                          value={constructionType}
                        />
                        <InfoRow
                          label='Classificação do Imóvel'
                          value={propertyClassification}
                        />
                        <InfoRow label='Objeto Segurado' value='Prédio' />
                      </div>
                    </div>
                  </>
                )}
            </div>
          </div>

          {/* Locais de Risco (Business) */}
          {isBusinessQuote && businessLocationCards.length > 0 && (
            <div className='flex flex-col gap-6'>
              <p className='font-montserrat text-base font-normal text-alba-black-500'>
                Lista de Locais
              </p>
              <div className='flex flex-col gap-4 w-full'>
                {businessLocationCards.map((location, index) => (
                  <BusinessLocationCard
                    key={location.id}
                    location={location}
                    locationNumber={index + 1}
                    isOpen={expandedLocationIds.has(location.id)}
                    canEdit={false}
                    onToggleLocation={() => toggleLocation(location.id)}
                    productId={productId}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Coberturas (Residencial) */}
          {!isBusinessQuote && coveragesEditable.length > 0 && (
            <div className='flex flex-col gap-6'>
              <p className='font-montserrat text-base font-normal text-alba-black-500'>
                Coberturas
              </p>

              <div className='flex flex-col gap-4'>
                {coveragesEditable.map((section, idx) => (
                  <CoverageCard
                    key={idx}
                    title={section.title}
                    items={section.items.map(item => ({
                      name: item.description,
                      value: item.limiteValue,
                    }))}
                  />
                ))}

                <CoverageCard
                  title='Assistências'
                  totalValue={assistancePurePremium}
                  items={assistanceItems.map(item => ({
                    name: item,
                  }))}
                />
              </div>
            </div>
          )}

          {/* Comercialização */}
          <CommercializationSection agents={proposalDetails?.agents || []} />

          {/* Botões de Ação */}
          <div className='flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end'>
            {canEditQuote && (
              <Button
                variant='secondary'
                size='md'
                className='w-full sm:w-auto'
                disabled={quoteWriteGuard.blocked}
                title={quoteWriteGuard.reason}
                onClick={() => {
                  if (!policyId) return;
                  navigate({
                    to: '/quotes/$policyId/edit',
                    params: { policyId },
                    search: { mode: 'edit' },
                  });
                }}
              >
                <SquarePen className='w-5 h-5' strokeWidth={2} />
                Editar
              </Button>
            )}

            {!isDisabled && (
              <>
                <ModalGeneratePDF
                  policyId={policyId}
                  productCode={proposalDetails?.productCode ?? ''}
                  stage='quote'
                  disabled={quoteActionGuard.blocked}
                  disabledReason={quoteActionGuard.reason}
                />

                {canWriteQuote && (
                  <ModalSendMail
                    productCode={proposalDetails?.productCode ?? ''}
                    policyId={policyId}
                    emailCorrector={proposalDetails?.agents?.[0]?.email ?? ''}
                    emailProponent={proposalDetails?.customer?.email ?? ''}
                    stage='quote'
                    disabled={quoteActionGuard.blocked}
                    disabledReason={quoteActionGuard.reason}
                  />
                )}

                {showCreateProposal && (
                  <Button
                    variant='secondary'
                    size='md'
                    className='w-full sm:w-auto'
                    title={proposalWriteGuard.reason}
                    disabled={
                      !canCreateProposal ||
                      isDisabled ||
                      proposalWriteGuard.blocked ||
                      initiateProposalMutation.isPending ||
                      validateInspectionJourneyMutation.isPending
                    }
                    onClick={handleCreateProposal}
                  >
                    <FileCheck className='h-5 w-5' strokeWidth={1.6} />
                    Criar Proposta
                  </Button>
                )}
              </>
            )}

            {isBusinessQuote && isClosed && refusalLetter?.url && (
              <Button
                variant='secondary'
                size='md'
                className='w-full sm:w-auto'
                onClick={handleDownloadRefusalLetter}
                loading={isRefusalLetterLoading}
                disabled={isRefusalLetterLoading}
              >
                <FileText className='h-5 w-5' strokeWidth={1.6} />
                Baixar Carta de Recusa
              </Button>
            )}

            <Button
              variant='primary'
              size='md'
              className='w-full sm:w-auto'
              onClick={() => push('/quotes')}
            >
              Sair
            </Button>
          </div>
        </div>
      </Page.Content>

      <CloneQuoteDialog
        isOpen={isCloneDialogOpen}
        isLoading={copyQuoteMutation.isPending}
        onClose={handleCloseCloneDialog}
        onConfirm={handleConfirmClone}
      />

      <InspectionJourneyModal
        isOpen={isInspectionJourneyModalOpen}
        onClose={() => setIsInspectionJourneyModalOpen(false)}
        onConfirm={handleInspectionJourneyConfirm}
        isLoading={
          validateInspectionJourneyMutation.isPending ||
          initiateProposalMutation.isPending
        }
      />
    </Page.Root>
  );
}

function Divider() {
  return <div className='h-px w-full bg-alba-gray-200' />;
}

export function CoverageCard({
  title,
  items,
  defaultExpanded = false,
}: {
  title?: string;
  items: CoverageItem[];
  totalValue?: string;
  defaultExpanded?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  function handleExpand() {
    setIsExpanded(prev => !prev);
  }

  return (
    <div className='flex flex-col gap-2 rounded-xl border border-alba-gray-400 p-4'>
      {title && (
        <>
          <div className='flex items-center justify-between'>
            <p className='flex-1 font-montserrat text-sm font-bold text-alba-black-500'>
              {title}
            </p>
          </div>

          <Divider />
        </>
      )}

      {isExpanded &&
        items.map((item, index) => (
          <div key={index} className='flex items-start justify-between'>
            <div className='flex flex-1 flex-col justify-center'>
              <p className='flex-1 font-montserrat text-xs font-normal text-alba-gray-600'>
                {item.name}
              </p>
            </div>
            {item.value !== undefined && (
              <div className='flex flex-col items-end justify-center'>
                <p className='font-montserrat text-xs font-normal text-alba-gray-600'>
                  {item.value}
                </p>
              </div>
            )}
          </div>
        ))}

      <div className='flex items-center justify-end gap-2'>
        <button
          className='flex items-center gap-2'
          type='button'
          onClick={handleExpand}
        >
          <div className='flex flex-1 items-center justify-end'>
            <p className='text-right font-montserrat text-xs font-normal text-alba-gray-600'>
              {isExpanded ? 'Menos' : 'Mais'} Detalhes
            </p>
          </div>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-alba-gray-600 transition-transform duration-300',
              isExpanded ? 'rotate-180' : ''
            )}
            strokeWidth={1.6}
          />
        </button>
      </div>
    </div>
  );
}
